// apps/web/src/shared/phoneExistsMeta.ts
//
// Defensive narrowing for the 409 `PHONE_ALREADY_EXISTS` error meta
// surfaced by `POST /leads` and `POST /clients` (#1363). The wire envelope
// carries `meta.existing = { type, id, publicCode }` — a PII-safe pointer
// to the colliding lead/client. It deliberately contains NO phone number,
// and consumers MUST NOT render one.

/**
 * @aiPurpose narrow the untyped `ApiClientError.meta` of a
 *   `PHONE_ALREADY_EXISTS` 409 into the PII-safe `existing` pointer.
 * @aiSecurity the returned shape carries only `{ type, id, publicCode }` —
 *   never a phone number. Callers route to the existing entity by id; they
 *   must never echo a raw phone.
 */
export interface PhoneExistsExisting {
  type: 'lead' | 'client';
  id: string;
  publicCode: string | null;
}

export function parsePhoneExistsMeta(meta: unknown): PhoneExistsExisting | null {
  if (meta == null || typeof meta !== 'object') return null;
  const existing = (meta as Record<string, unknown>)['existing'];
  if (existing == null || typeof existing !== 'object') return null;
  const e = existing as Record<string, unknown>;
  const type = e['type'];
  const id = e['id'];
  const publicCode = e['publicCode'];
  if (type !== 'lead' && type !== 'client') return null;
  if (typeof id !== 'string') return null;
  if (publicCode !== null && typeof publicCode !== 'string') return null;
  return { type, id, publicCode };
}
